import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { InboxEntry } from '@this+that/models';
import { listNewReplyMessageIds } from '@this+that/utils';
import log from 'loglevel';

import { useIsEntryOwner } from './useIsEntryOwner';
import { useDismissInboxEntryRepliesService } from '../../services/inboxEntries.service';
import { useInboxEntriesStore } from '../../store';
import { useCustomToast } from '../useCustomToast';

type DismissableEntry = Pick<InboxEntry, 'id' | 'createdFor' | 'newReplies'>;

const activeTaskGroupSlice = () =>
  useInboxEntriesStore.getState().taskManager.activeTaskGroup;

// Clears the new replies the viewer sees until someone writes again. Owner only, optimistic,
// pinned to the ids on screen (`visibleMessageIds` when the surface shows only some of them),
// so a reply that lands meanwhile still lights.
export const useDismissEntryNewReplies = (
  inboxEntry: DismissableEntry,
  visibleMessageIds?: ReadonlySet<string>,
) => {
  const { t } = useTranslation();
  const { errorMessage } = useCustomToast();
  const { dismissInboxEntryReplies } = useDismissInboxEntryRepliesService();
  const isOwner = useIsEntryOwner(inboxEntry);
  const [isDismissing, setIsDismissing] = useState(false);
  const { id, newReplies } = inboxEntry;

  const messageIds = useMemo(() => {
    const listed = listNewReplyMessageIds(newReplies);
    return visibleMessageIds
      ? listed.filter((messageId) => visibleMessageIds.has(messageId))
      : listed;
  }, [newReplies, visibleMessageIds]);

  const dismiss = useCallback(async () => {
    if (!isOwner || !messageIds.length) {
      return;
    }
    const previous = activeTaskGroupSlice().dismissEntryNewReplies(
      id,
      messageIds,
    );
    setIsDismissing(true);
    try {
      const current = await dismissInboxEntryReplies(id, messageIds);
      if (previous && current?.newReplies) {
        activeTaskGroupSlice().settleDismissedReplies(
          previous,
          current.newReplies,
        );
      }
    } catch (error) {
      log.error('Error dismissing new replies:', error);
      if (previous) {
        activeTaskGroupSlice().restoreDismissedReplies(previous);
      }
      errorMessage(t('doBox.newReply.dismissError'));
    } finally {
      setIsDismissing(false);
    }
  }, [id, isOwner, messageIds, dismissInboxEntryReplies, t]);

  return {
    canDismiss: isOwner && messageIds.length > 0,
    dismiss,
    isDismissing,
    messageCount: messageIds.length,
  };
};
